import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Paper,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import FolderIcon from '@mui/icons-material/Folder';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import { sendDocumentFolder, getWorksSummaryForActivity, getActivityDetails } from '../../services/authService';

interface Work {
    id_work: string;
    description: string;
    documentsCount: number;
}

export const DocumentFolder: React.FC = () => {
    const [selectedActivityId, setSelectedActivityId] = useState<string>('');
    const [activityName, setActivityName] = useState<string>('');
    const [works, setWorks] = useState<Work[]>([]);
    const [totalDocuments, setTotalDocuments] = useState<number>(0);
    const [recipientEmail, setRecipientEmail] = useState<string>('');
    const [ccEmail, setCcEmail] = useState<string>('');
    const [emailSubject, setEmailSubject] = useState<string>('');
    const [emailBody, setEmailBody] = useState<string>('');
    const [sendSuccess, setSendSuccess] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [selectedWorkIds, setSelectedWorkIds] = useState<Set<string>>(new Set());
    const [userRole, setUserRole] = useState<string>('');

    // Recupera ruolo utente
    useEffect(() => {
        const role = sessionStorage.getItem('userRole') || '';
        setUserRole(role);
    }, []);

    // Ascolta il cambio di attività
    useEffect(() => {
        const handleActivityChanged = (event: any) => {
            const activityId = event.detail;
            if (activityId) {
                setSelectedActivityId(activityId);
                loadWorksSummary(activityId);
            }
        };

        window.addEventListener('activityChanged', handleActivityChanged);

        // Carica attività da sessionStorage al primo mount
        const activityId = sessionStorage.getItem('selectedActivityId');
        if (activityId) {
            setSelectedActivityId(activityId);
            loadWorksSummary(activityId);
        }

        return () => window.removeEventListener('activityChanged', handleActivityChanged);
    }, []);

    // Genera oggetto email di default quando cambia l'attività o la selezione
    useEffect(() => {
        if (activityName && works.length > 0) {
            const selectedWorks = works.filter(w => selectedWorkIds.has(w.id_work));
            const selectedDocsCount = selectedWorks.reduce((sum, w) => sum + w.documentsCount, 0);
            
            setEmailSubject(`Fascicolo Elettronico - ${activityName}`);
            setEmailBody(
                `Gentile destinatario,\n\n` +
                `in allegato il fascicolo elettronico per l'attività "${activityName}".\n\n` +
                `Il fascicolo contiene ${selectedDocsCount} documenti relativi a ${selectedWorks.length} pratiche selezionate.\n\n` +
                `Troverai i link per scaricare i documenti (validi per 1 ora).\n\n` +
                `Cordiali saluti`
            );
        }
    }, [activityName, works, selectedWorkIds]);

    const loadWorksSummary = async (activityId: string) => {
        setErrorMessage('');
        
        try {
            console.log('[DocumentFolder] Caricamento pratiche per activityId:', activityId);
            
            // Leggi ruolo DIRETTAMENTE da sessionStorage (non usare state userRole che potrebbe non essere ancora popolato)
            const currentUserRole = sessionStorage.getItem('userRole') || '';
            console.log('[DocumentFolder] UserRole da sessionStorage:', currentUserRole);
            
            // Se Admin, recupera l'userId selezionato
            let targetUserId: string | null = null;
            if (currentUserRole === 'Admin') {
                targetUserId = sessionStorage.getItem('selectedUserId');
                console.log('[DocumentFolder] Admin - selectedUserId:', targetUserId);
            }
            
            // Prova a recuperare dall'attività da sessionStorage
            const activitiesData = sessionStorage.getItem('activities');
            let activityInfo = '';
            
            console.log('[DocumentFolder] Activities in sessionStorage:', activitiesData ? 'presenti' : 'NON presenti');
            
            if (activitiesData) {
                const activities = JSON.parse(activitiesData);
                console.log('[DocumentFolder] Numero attività:', activities.length);
                console.log('[DocumentFolder] Cerco activityId:', activityId);
                
                const selectedActivity = activities.find((act: any) => act.activityId === activityId);
                
                if (selectedActivity) {
                    console.log('[DocumentFolder] Attività trovata in sessionStorage:', selectedActivity);
                    activityInfo = `${selectedActivity.indirizzo}, ${selectedActivity.citta}`;
                } else {
                    console.warn('[DocumentFolder] Attività NON trovata in sessionStorage');
                }
            }
            
            // Se non trovata in sessionStorage (es. Admin), chiama API
            if (!activityInfo) {
                console.log('[DocumentFolder] Recupero attività da API...');
                try {
                    const activityDetails = await getActivityDetails(activityId, targetUserId);
                    console.log('[DocumentFolder] Attività recuperata da API:', activityDetails);
                    activityInfo = `${activityDetails.indirizzo}, ${activityDetails.citta}`;
                } catch (apiError: any) {
                    console.error('[DocumentFolder] Errore recupero attività da API:', apiError);
                    activityInfo = 'Seleziona un\'attività';
                }
            }
            
            // Chiamata API per ottenere i lavori con conteggio documenti
            // Se Admin e ha selezionato un userId, passalo alla query
            const response = await getWorksSummaryForActivity(activityId, targetUserId);
            
            const worksData: Work[] = response.works.map(w => ({
                id_work: w.id_work,
                description: `${w.work_name}${w.description ? ' - ' + w.description : ''}`,
                documentsCount: Number.isFinite(Number(w.documentsCount))
                    ? Number(w.documentsCount)
                    : Number.isFinite(Number(w.completion_count))
                        ? Number(w.completion_count)
                        : 0
            }));
            
            setWorks(worksData);
            setTotalDocuments(worksData.reduce((sum, w) => sum + w.documentsCount, 0));
            setActivityName(activityInfo);
            
            // Seleziona tutte le pratiche di default
            setSelectedWorkIds(new Set(worksData.map(w => w.id_work)));
            
        } catch (error: any) {
            console.error('Errore caricamento riepilogo pratiche:', error);
            setErrorMessage(error.message || 'Errore durante il caricamento dei dati');
        }
    };

    const handleToggleWork = (workId: string) => {
        setSelectedWorkIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(workId)) {
                newSet.delete(workId);
            } else {
                newSet.add(workId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        if (selectedWorkIds.size === works.length) {
            // Deseleziona tutti
            setSelectedWorkIds(new Set());
        } else {
            // Seleziona tutti
            setSelectedWorkIds(new Set(works.map(w => w.id_work)));
        }
    };

    const handleSendEmail = async () => {
        if (!recipientEmail) {
            setErrorMessage('Inserisci almeno un indirizzo email destinatario');
            return;
        }

        if (selectedWorkIds.size === 0) {
            setErrorMessage('Seleziona almeno una pratica da includere nell\'invio');
            return;
        }

        setErrorMessage('');
        setSendSuccess(false);

        try {
            // Chiamata API reale per inviare fascicolo elettronico con link ai documenti
            const result = await sendDocumentFolder({
                workIds: Array.from(selectedWorkIds),
                recipientEmail,
                ccEmail: ccEmail || undefined,
                subject: emailSubject,
                message: emailBody
            });

            console.log('✅ Email inviata:', result);
            setSendSuccess(true);
            
            // Reset form dopo 3 secondi
            setTimeout(() => {
                setSendSuccess(false);
                setRecipientEmail('');
                setCcEmail('');
            }, 3000);

        } catch (error: any) {
            console.error('❌ Errore invio email:', error);
            setErrorMessage(error.message || 'Errore durante l\'invio della email');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#8B6F47', fontWeight: 600 }}>
                    Fascicolo Elettronico
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Genera e invia via email link per scaricare i documenti delle pratiche selezionate (validità 1 ora)
                </Typography>
                {activityName && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '4px solid #8B6F47' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#8B6F47' }}>
                            Attività selezionata:
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                            {activityName}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Layout a due colonne */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                
                {/* Colonna Sinistra - Riepilogo Attività e Documenti */}
                <Card sx={{ flex: '1 1 45%', minWidth: 400 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <FolderIcon sx={{ mr: 1, color: '#8B6F47' }} />
                            <Typography variant="h6">
                                {activityName || 'Caricamento...'}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <>
                                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                    <Paper elevation={2} sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ color: '#8B6F47', fontWeight: 600 }}>
                                            {selectedWorkIds.size}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Pratiche Selezionate
                                        </Typography>
                                    </Paper>
                                    <Paper elevation={2} sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ color: '#8B6F47', fontWeight: 600 }}>
                                            {works.filter(w => selectedWorkIds.has(w.id_work)).reduce((sum, w) => sum + w.documentsCount, 0)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Documenti Selezionati
                                        </Typography>
                                    </Paper>
                                </Box>

                                {/* Lista Pratiche */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Dettaglio Pratiche:
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={works.length > 0 && selectedWorkIds.size === works.length}
                                                indeterminate={selectedWorkIds.size > 0 && selectedWorkIds.size < works.length}
                                                onChange={handleSelectAll}
                                                sx={{
                                                    color: '#8B6F47',
                                                    '&.Mui-checked': { color: '#8B6F47' },
                                                    '&.MuiCheckbox-indeterminate': { color: '#8B6F47' }
                                                }}
                                            />
                                        }
                                        label="Seleziona tutti"
                                    />
                                </Box>
                                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    {works.map((work) => (
                                        <ListItem 
                                            key={work.id_work} 
                                            sx={{ 
                                                border: '1px solid #e0e0e0', 
                                                borderRadius: 1, 
                                                mb: 1,
                                                bgcolor: selectedWorkIds.has(work.id_work) ? 'rgba(139, 111, 71, 0.08)' : 'transparent'
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedWorkIds.has(work.id_work)}
                                                onChange={() => handleToggleWork(work.id_work)}
                                                sx={{
                                                    color: '#8B6F47',
                                                    '&.Mui-checked': { color: '#8B6F47' }
                                                }}
                                            />
                                            <ListItemIcon>
                                                <AttachFileIcon sx={{ color: '#8B6F47' }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={work.description}
                                                secondary={`ID: ${work.id_work}`}
                                            />
                                            <Chip 
                                                label={`${work.documentsCount} doc.`}
                                                size="small"
                                                sx={{ bgcolor: '#8B6F47', color: 'white' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                        </>
                    </CardContent>
                </Card>

                {/* Colonna Destra - Form Invio Email */}
                <Card sx={{ flex: '1 1 45%', minWidth: 400 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <EmailIcon sx={{ mr: 1, color: '#8B6F47' }} />
                        <Typography variant="h6">
                            Invia Fascicolo via Email
                        </Typography>
                    </Box>

                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                            {errorMessage}
                        </Alert>
                    )}

                    {sendSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
                            Email inviata con successo!
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Destinatario *"
                            placeholder="email@example.com"
                            fullWidth
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            disabled={totalDocuments === 0}
                        />

                        <TextField
                            label="CC (opzionale)"
                            placeholder="email@example.com"
                            fullWidth
                            value={ccEmail}
                            onChange={(e) => setCcEmail(e.target.value)}
                            disabled={totalDocuments === 0}
                        />

                        <TextField
                            label="Oggetto"
                            fullWidth
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            disabled={totalDocuments === 0}
                        />

                        <TextField
                            label={userRole === 'Admin' ? "Messaggio" : "Messaggio (solo Admin può modificare)"}
                            multiline
                            rows={6}
                            fullWidth
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value)}
                            disabled={totalDocuments === 0 || userRole !== 'Admin'}
                            helperText={userRole !== 'Admin' ? 'Solo gli amministratori possono modificare il messaggio' : ''}
                        />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<SendIcon />}
                                onClick={handleSendEmail}
                                disabled={totalDocuments === 0 || !recipientEmail}
                                sx={{
                                    bgcolor: '#8B6F47',
                                    '&:hover': { bgcolor: '#6B5635' },
                                    px: 4
                                }}
                            >
                                Invia Email
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            
            </Box>
        </Box>
    );
};
